import { Injectable } from "@angular/core";
import { Observable, throwError, BehaviorSubject } from "rxjs";
import { catchError, tap, map } from "rxjs/operators";
import { ApiService } from "./api/api.service";
import { API_ENDPOINTS } from "./api/api-endpoints";
import { environment } from "../../../environments/environment";

export interface PaymentCard {
  id?: number;
  first_name: string;
  last_name: string;
  card_number: string;
  expiry_date: string;
  cvc?: string;
  is_primary: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ApiResponse<T = any> {
  data?: T;
  message?: string;
  error?: string;
  results?: T[];
  count?: number;
}

export interface SaveCardTokenPayload {
  token: string;
  first_name: string;
  last_name: string;
  is_primary: boolean;
}

@Injectable({
  providedIn: "root",
})
export class PaymentCardService {
  private baseUrl = API_ENDPOINTS.customer.accountSettings.sameurl;
  private apiUrl = environment.apiUrl;

  // State management for cards list
  private cardsSubject = new BehaviorSubject<PaymentCard[]>([]);
  public cards$ = this.cardsSubject.asObservable();

  constructor(private apiService: ApiService) {}

  /**
   * Fetch all payment cards for the logged-in user
   */
  getAllCards(): Observable<PaymentCard[]> {
    return this.apiService.request<any>(this.baseUrl, "GET").pipe(
      map((response: any) => {
        // Handle different response structures
        const cards = response?.results || response?.data || response || [];
        return Array.isArray(cards) ? cards : [];
      }),
      tap((cards: PaymentCard[]) => this.cardsSubject.next(cards)),
      catchError(this.handleError),
    );
  }

  // Saves a card using Square token (NO card_number/expiry/cvc)
  saveCardWithSquareToken(payload: SaveCardTokenPayload) {
    const url = `${this.apiUrl}/integrations/square/save-card/`; // adjust to your API route
    return this.apiService.request<any>(url, "POST", payload).pipe(
      map((response: any) => response?.data || response),
      catchError(this.handleError),
    );
  }

  // Updates only metadata (no sensitive fields)
  updateCardMeta(id: number, payload: Partial<PaymentCard>) {
    const url = `${this.baseUrl}${id}/`;
    return this.apiService.request<any>(url, "PATCH", payload).pipe(
      map((response: any) => response?.data || response),
      catchError(this.handleError),
    );
  }

  /**
   * Create a new payment card
   */
  createCard(
    card: PaymentCard,
    showSuccessMsg: boolean = true,
  ): Observable<PaymentCard> {
    const successMsg = showSuccessMsg
      ? "Payment card added successfully!"
      : undefined;

    return this.apiService
      .request<any>(this.baseUrl, "POST", card, successMsg)
      .pipe(
        map((response: any) => response?.data || response),
        tap((newCard: PaymentCard) => {
          const currentCards = this.cardsSubject.value;
          // If new card is primary, update other cards
          if (newCard.is_primary) {
            const updatedCards = currentCards.map((c: PaymentCard) => ({
              ...c,
              is_primary: false,
            }));
            this.cardsSubject.next([...updatedCards, newCard]);
          } else {
            this.cardsSubject.next([...currentCards, newCard]);
          }
        }),
        catchError(this.handleError),
      );
  }

  /**
   * Update an existing payment card
   */
  updateCard(
    id: number,
    card: Partial<PaymentCard>,
    showSuccessMsg: boolean = true,
  ): Observable<PaymentCard> {
    const url = `${this.baseUrl}${id}/`;
    const successMsg = showSuccessMsg
      ? "Payment card updated successfully!"
      : undefined;

    return this.apiService.request<any>(url, "PATCH", card, successMsg).pipe(
      map((response: any) => response?.data || response),
      tap((updatedCard: PaymentCard) => {
        const currentCards = this.cardsSubject.value;
        const index = currentCards.findIndex((c: PaymentCard) => c.id === id);

        if (index !== -1) {
          let newCards = [...currentCards];

          // If updated card is primary, update other cards
          if (updatedCard.is_primary) {
            newCards = newCards.map((c: PaymentCard) =>
              c.id === id ? updatedCard : { ...c, is_primary: false },
            );
          } else {
            newCards[index] = updatedCard;
          }

          this.cardsSubject.next(newCards);
        }
      }),
      catchError(this.handleError),
    );
  }

  /**
   * Delete a payment card
   */
  deleteCard(id: number, showSuccessMsg: boolean = true): Observable<void> {
    const url = `${this.baseUrl}${id}/`;
    const successMsg = showSuccessMsg
      ? "Payment card deleted successfully!"
      : undefined;

    return this.apiService
      .request<void>(url, "DELETE", undefined, successMsg)
      .pipe(
        tap(() => {
          const currentCards = this.cardsSubject.value.filter(
            (c: PaymentCard) => c.id !== id,
          );
          this.cardsSubject.next(currentCards);
        }),
        catchError(this.handleError),
      );
  }

  /**
   * Set a card as primary
   */
  setPrimaryCard(id: number): Observable<PaymentCard> {
    return this.updateCard(id, { is_primary: true }, true);
  }

  /**
   * Get masked card number for display
   */
  getMaskedCardNumber(cardNumber: string): string {
    if (!cardNumber || cardNumber.length < 4) return "****";
    return `**** **** **** ${cardNumber.slice(-4)}`;
  }

  /**
   * Get card brand from card number
   */
  getCardBrand(cardNumber: string): string {
    if (!cardNumber) return "unknown";

    const cleanNumber = cardNumber.replace(/\s+/g, "");

    /// old logic

    // if (/^4/.test(cleanNumber)) return 'visa';
    // if (/^5[1-5]/.test(cleanNumber)) return 'mastercard';
    // if (/^3[47]/.test(cleanNumber)) return 'amex';
    // if (/^6(?:011|5)/.test(cleanNumber)) return 'discover';

    /// new logic

    // Visa
    if (/^4\d{6,}$/.test(cleanNumber)) return "visa";

    // Mastercard (51–55 and 2221–2720)
    if (
      /^(5[1-5]\d{4,}|2(2[2-9]\d{3}|2[3-9]\d{4}|[3-6]\d{5}|7([01]\d{4}|20\d{3})))/.test(
        cleanNumber,
      )
    ) {
      return "mastercard";
    }

    // American Express
    if (/^3[47]\d{5,}$/.test(cleanNumber)) return "amex";

    // Discover
    if (/^(6011|65|64[4-9])\d{3,}$/.test(cleanNumber)) return "discover";

    // JCB (3528–3589)
    if (/^35(2[89]|[3-8]\d)\d{4,}$/.test(cleanNumber)) return "jcb";

    // Diners Club (300–305, 36, 38–39)
    if (/^(3(0[0-5]|[68]\d))\d{4,}$/.test(cleanNumber)) return "diners";

    // Maestro (most common prefixes)
    if (
      /^(5018|5020|5038|56|57|58|6304|6759|676[1-3])\d{4,}$/.test(cleanNumber)
    ) {
      return "maestro";
    }

    return "unknown";
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: any): Observable<never> {
    let errorMessage = "An error occurred";

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage =
        error.error?.message ||
        error.error?.error ||
        error.message ||
        `Server returned code ${error.status}`;
    }

    console.error("PaymentCardService Error:", errorMessage, error);
    return throwError(() => new Error(errorMessage));
  }
}
